import { create } from 'zustand';
import { auth, googleAuthProvider, /* db */ isValidApiKey } from '@/firebase/firebase';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  signInWithPopup,
  type User as FirebaseUser,
  type AuthError
} from 'firebase/auth';
import { 
  updateUserProfileDocument, 
  getUserProfileDocument, 
  type UserProfile, 
  /* createUserSettingsDocument */
} from '@/firebase/firestoreService';

interface AuthState {
  user: FirebaseUser | null;
  userProfile: UserProfile | null;
  isLoadingAuth: boolean;
  authError: AuthError | null | Error | string;
  isOfflineMode: boolean;
  initializeAuthListener: () => () => void;
  loginWithEmail: (email: string, pass: string) => Promise<void>;
  signupWithEmail: (email: string, pass: string, displayName?: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  clearAuthError: () => void;
  refreshUserProfile: () => Promise<void>;
}

const offlineErrorMsg = "Firebase is not configured. Please set up API keys in .env to use this feature.";

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  userProfile: null,
  isLoadingAuth: !isValidApiKey,
  authError: null,
  isOfflineMode: !isValidApiKey,

  initializeAuthListener: () => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && isValidApiKey) {
        set({ user: firebaseUser, isLoadingAuth: true, authError: null });
        try {
          await updateUserProfileDocument(firebaseUser, { displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] });
          const profile = await getUserProfileDocument(firebaseUser.uid);
          set({ userProfile: profile, isLoadingAuth: false });
        } catch (error) {
          console.error("Error fetching/updating user profile:", error);
          set({ userProfile: null, isLoadingAuth: false, authError: error as Error });
        }
      } else {
        set({ user: null, userProfile: null, isLoadingAuth: false, authError: null });
      }
    });
    return unsubscribe;
  },

  refreshUserProfile: async () => {
    const { user } = get();
    if (user && isValidApiKey) {
      set({ isLoadingAuth: true });
      try {
        const profile = await getUserProfileDocument(user.uid);
        set({ userProfile: profile, isLoadingAuth: false, authError: null });
      } catch (error) {
        console.error("Error refreshing user profile:", error);
        set({ isLoadingAuth: false, authError: error as Error });
      }
    } else if (!isValidApiKey) {
       set({ authError: "Cannot refresh profile in offline mode." });
    }
  },

  loginWithEmail: async (email, password) => {
    if (!isValidApiKey) {
      set({ authError: offlineErrorMsg, isLoadingAuth: false });
      console.warn(offlineErrorMsg);
      throw new Error(offlineErrorMsg);
    }
    set({ isLoadingAuth: true, authError: null });
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      set({ authError: error as AuthError, isLoadingAuth: false, user: null, userProfile: null });
      console.error("Login error:", error);
      throw error;
    }
  },

  signupWithEmail: async (email, password, displayName) => {
    if (!isValidApiKey) {
      set({ authError: offlineErrorMsg, isLoadingAuth: false });
      console.warn(offlineErrorMsg);
      throw new Error(offlineErrorMsg);
    }
    set({ isLoadingAuth: true, authError: null });
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      if (userCredential.user) {
        await updateUserProfileDocument(userCredential.user, { 
          displayName: displayName || userCredential.user.email?.split('@')[0] 
        });
      }
    } catch (error) {
      set({ authError: error as AuthError, isLoadingAuth: false, user: null, userProfile: null });
      console.error("Signup error:", error);
      throw error;
    }
  },

  loginWithGoogle: async () => {
    if (!isValidApiKey || !googleAuthProvider) {
      set({ authError: offlineErrorMsg, isLoadingAuth: false });
      console.warn(offlineErrorMsg + " (Google Auth Provider not available)");
      throw new Error(offlineErrorMsg + " (Google Auth Provider not available)");
    }
    set({ isLoadingAuth: true, authError: null });
    try {
      await signInWithPopup(auth, googleAuthProvider);
    } catch (error) {
      set({ authError: error as AuthError, isLoadingAuth: false, user: null, userProfile: null });
      console.error("Google login error:", error);
      throw error;
    }
  },

  logout: async () => {
    if (!isValidApiKey) {
      set({ user: null, userProfile: null, authError: offlineErrorMsg, isLoadingAuth: false });
      console.warn(offlineErrorMsg);
      return;
    }
    set({ isLoadingAuth: true });
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      set({ authError: error as AuthError, isLoadingAuth: false });
      console.error("Logout error:", error);
      throw error;
    }
  },
  clearAuthError: () => {
    set({ authError: null });
  }
}));

useAuthStore.getState().initializeAuthListener(); 